import { useQueryClient } from '@tanstack/react-query';
import type { Href } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { BackButton } from '../../../components/primitives/BackButton';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Heading2, Heading4, Label } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { voiceAgentsApi } from '../api';
import { useLatestPreviewSession, usePreviewSessions } from '../hooks';
import { useAgentPreview } from '../hooks/useAgentPreview';
import type { VoiceCallTranscriptEntry } from '../types';
import { AgentVoiceVisualizer } from './AgentVoiceVisualizer';
import { PreviewToolCallCard } from './PreviewToolCallCard';
import { TranscriptPanel } from './TranscriptPanel';

interface AgentPreviewCallProps {
  voiceAgentId: string;
  agentName?: string;
  /** Logical parent to return to when the back control is pressed. */
  backFallbackHref?: Href;
}

const STATUS_BADGE_COLORS: Record<string, 'cyan' | 'amber' | 'green' | 'red' | 'neutral'> = {
  idle: 'neutral',
  connecting: 'amber',
  connected: 'green',
  disconnected: 'neutral',
  error: 'red',
};

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatEndedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Compact single-line label for a session-history chip (e.g. "Jul 21, 6:31 PM").
 * The full timestamp stays in the chip's accessibility label and the panel header.
 */
function formatSessionChipLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Pull a human-readable message out of an Axios/NestJS error response
 * (`{ message: string | string[] }`) so preview-start failures show the
 * backend's reason (e.g. "agent has not been published to ElevenLabs") instead
 * of a generic fallback. Returns null when no usable message is present.
 */
function extractApiErrorMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  const message = data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(', ') || null;
  if (typeof message === 'string') return message || null;
  return null;
}

// Mirrors PREVIEW_TTL_SECONDS in backend-nest/src/voice-agents/services/preview.service.ts.
// The DB-backed VoicePreviewSession.expiresAt is createdAt + this TTL and is not
// renewed on end() — so a session is resumable only within this window from
// when it was originally created, regardless of when it was ended.
const PREVIEW_RESUME_WINDOW_SECONDS = 1200;

function isWithinResumeWindow(createdAtIso: string): boolean {
  const createdAtMs = new Date(createdAtIso).getTime();
  if (Number.isNaN(createdAtMs)) return false;
  return Date.now() - createdAtMs < PREVIEW_RESUME_WINDOW_SECONDS * 1000;
}

function NotWebCard({ agentName, voiceAgentId }: { agentName?: string; voiceAgentId: string }) {
  const { t } = useTranslation();
  const previewUrl =
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.href
      : `https://app.flowsell.ai/voice-agents/${voiceAgentId}/preview`;

  function sendEmailLink() {
    const subject = encodeURIComponent(
      `${t('voiceAgents.previewCall.emailSubjectPrefix')} ${agentName ?? t('voiceAgents.previewCall.voiceAgentFallback')}`,
    );
    const body = encodeURIComponent(`${t('voiceAgents.previewCall.emailBody')}\n\n${previewUrl}\n`);
    Linking.openURL(`mailto:?subject=${subject}&body=${body}`).catch(() => {
      // best-effort: ignore failures opening the mail client
    });
  }

  return (
    <View style={cardStyles.container} accessible accessibilityRole="none">
      <VStack spacing="lg" style={cardStyles.inner}>
        <Heading4 style={cardStyles.title}>
          {t('voiceAgents.previewCall.browserOnlyTitle')}
        </Heading4>
        <Body style={cardStyles.body}>
          {t('voiceAgents.previewCall.browserOnlyBodyPrefix')}{' '}
          {agentName ?? t('voiceAgents.previewCall.thisAgentFallback')}{' '}
          {t('voiceAgents.previewCall.browserOnlyBodySuffix')}
        </Body>
        <Button
          variant="primary"
          onPress={sendEmailLink}
          accessibilityLabel={t('voiceAgents.previewCall.sendEmailA11y')}
          accessibilityRole="button"
        >
          {t('voiceAgents.previewCall.sendEmail')}
        </Button>
      </VStack>
    </View>
  );
}

const cardStyles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: colors.background.primary,
  },
  inner: {
    maxWidth: 400,
    backgroundColor: colors.background.elevated,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  title: {
    color: colors.text.primary,
    textAlign: 'center',
  },
  body: {
    color: colors.text.secondary,
    textAlign: 'center',
  },
});

export function AgentPreviewCall({
  voiceAgentId,
  agentName,
  backFallbackHref,
}: AgentPreviewCallProps) {
  if (Platform.OS !== 'web') {
    return <NotWebCard agentName={agentName} voiceAgentId={voiceAgentId} />;
  }

  return (
    <AgentPreviewCallWeb
      voiceAgentId={voiceAgentId}
      agentName={agentName}
      backFallbackHref={backFallbackHref}
    />
  );
}

function AgentPreviewCallWeb({ voiceAgentId, agentName, backFallbackHref }: AgentPreviewCallProps) {
  const { t } = useTranslation();
  const backControl = backFallbackHref ? (
    <BackButton
      accessibilityLabel={t('common.actions.goBack')}
      tooltip={t('common.actions.goBack')}
      fallbackHref={backFallbackHref}
    />
  ) : null;
  const { currentOrganization } = useAuth();
  const { showError } = useToast();
  const orgId = currentOrganization?.id;
  const {
    connectionStatus,
    errorKind,
    transcript,
    toolCallLog,
    isMicActive,
    isAgentSpeaking,
    start,
    end,
    toggleMic,
  } = useAgentPreview(voiceAgentId);

  const queryClient = useQueryClient();
  const { data: latestSession } = useLatestPreviewSession(voiceAgentId);
  const { data: previewSessions } = usePreviewSessions(voiceAgentId);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // History of finished preview sessions (FLOSEAI-579). Falls back to the
  // latest session so behaviour is unchanged when the list is still loading or
  // only one session exists. Older sessions are read-only (their live provider
  // session has expired past the resume TTL); only the latest is resumable.
  const historySessions =
    previewSessions && previewSessions.length > 0
      ? previewSessions
      : latestSession
        ? [latestSession]
        : [];
  const displayedSession =
    historySessions.find((s) => s.id === selectedSessionId) ?? historySessions[0] ?? null;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Resume availability is sourced from the DB-backed latest finished session,
  // NOT from localStorage. This is what survives "End preview" (useAgentPreview's
  // end() no longer wipes it) and a page reload — localStorage never carried
  // the persisted transcript reliably across those events.
  const canResume = !!latestSession && isWithinResumeWindow(latestSession.createdAt);

  useEffect(() => {
    if (connectionStatus === 'connected') {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [connectionStatus]);

  async function handleStart() {
    setElapsedSeconds(0);
    setIsStarted(true);
    try {
      await start();
    } catch (err) {
      // A failed preview start (e.g. 400 "agent not published to ElevenLabs")
      // must surface as a toast, not an uncaught error that crashes the screen.
      setIsStarted(false);
      showError(extractApiErrorMessage(err) ?? t('voiceAgents.previewCall.startFailed'));
    }
  }

  async function handleResume() {
    if (!latestSession || !canResume) return;
    setIsStarted(true);

    // ElevenLabs signed URLs expire in minutes; the DB-backed session (TTL 20
    // min) outlives them. Always mint a fresh signed URL via getPreviewConfig
    // before opening the WebSocket, otherwise the handshake is silently
    // rejected and the UI gets stuck on "Conectando…".
    if (!orgId) {
      setIsStarted(false);
      showError(t('voiceAgents.previewCall.resumeFailed'));
      return;
    }
    try {
      const fresh = await voiceAgentsApi.getPreviewConfig(voiceAgentId, orgId, latestSession.id);
      // Seed the live transcript panel from the DB session's transcript — the
      // prior conversation, not whatever (possibly stale/absent) copy
      // localStorage may hold.
      await start(
        latestSession.id,
        fresh.convaiSignedUrl,
        fresh.mockLeadContext,
        latestSession.transcript,
      );
    } catch (err) {
      console.warn('[preview] resume failed — DB session is no longer valid', err);
      setIsStarted(false);
      showError(t('voiceAgents.previewCall.resumeFailed'));
    }
  }

  async function handleEnd() {
    await end();
    setIsStarted(false);
    setElapsedSeconds(0);
    // Snap the history back to the newly-finished session (drop any manual
    // older-session selection) and refresh both the latest-session and the
    // history-list queries so the just-ended session shows up (FLOSEAI-579).
    setSelectedSessionId(null);
    queryClient.invalidateQueries({ queryKey: ['preview-latest', voiceAgentId] });
    queryClient.invalidateQueries({ queryKey: ['preview-sessions', voiceAgentId] });
  }

  const transcriptEntries: VoiceCallTranscriptEntry[] = transcript.map((e, i) => ({
    id: `preview-${i}`,
    voiceCallId: '',
    turnIndex: i,
    speaker: e.speaker,
    text: e.text,
    startedAtMs: 0,
    endedAtMs: 0,
  }));

  const previousSessionEntries: VoiceCallTranscriptEntry[] = (
    displayedSession?.transcript ?? []
  ).map((e, i) => ({
    id: `prev-${i}`,
    voiceCallId: displayedSession?.id ?? '',
    turnIndex: i,
    speaker: e.speaker,
    text: e.text,
    startedAtMs: 0,
    endedAtMs: 0,
  }));

  const statusLabel = (status: string) => {
    const key = `voiceAgents.previewCall.status.${status}`;
    const label = t(key);
    return label === key ? status : label;
  };

  if (!isStarted) {
    return (
      <View style={styles.root}>
        {backControl ? <View style={styles.backRow}>{backControl}</View> : null}
        <View style={styles.idleLayout}>
          <VStack style={styles.startScreen} spacing="xl">
            {canResume ? (
              <>
                <Heading2 style={styles.headingCenter}>
                  {t('voiceAgents.previewCall.resumeFoundTitle')}
                </Heading2>
                <Body style={styles.bodyCenter}>
                  {t('voiceAgents.previewCall.resumeFoundBody')}
                </Body>
                <HStack spacing="md">
                  <Button
                    variant="outline"
                    onPress={handleStart}
                    accessibilityLabel={t('voiceAgents.previewCall.newSessionA11y')}
                  >
                    {t('voiceAgents.previewCall.newSession')}
                  </Button>
                  <Button
                    variant="primary"
                    onPress={handleResume}
                    accessibilityLabel={t('voiceAgents.previewCall.resumeSessionA11y')}
                  >
                    {t('voiceAgents.previewCall.resumeSession')}
                  </Button>
                </HStack>
              </>
            ) : (
              <>
                <AgentVoiceVisualizer isActive={false} isAgentSpeaking={false} />
                <Heading2 style={styles.headingCenter}>
                  {agentName ?? t('voiceAgents.previewCall.previewTitle')}
                </Heading2>
                <Body style={styles.bodyCenter}>
                  {t('voiceAgents.previewCall.startDescription')}
                </Body>
                <Button
                  variant="primary"
                  size="lg"
                  onPress={handleStart}
                  accessibilityLabel={t('voiceAgents.previewCall.startPreviewA11y')}
                  accessibilityRole="button"
                >
                  {t('voiceAgents.previewCall.startPreview')}
                </Button>
              </>
            )}
          </VStack>
          {displayedSession && previousSessionEntries.length > 0 && (
            <View
              style={styles.rightPanel}
              accessible
              accessibilityLabel={t('voiceAgents.previewCall.previousSessionA11y')}
            >
              {/* history list + read-only transcript of the selected session */}
              <View style={styles.prevSessionHeader}>
                <Heading4 style={styles.panelTitle} accessibilityRole="header">
                  {historySessions.length > 1
                    ? t('voiceAgents.previewCall.sessionHistory')
                    : t('voiceAgents.previewCall.previousSession')}
                </Heading4>
                {historySessions.length <= 1 && (
                  <Caption style={styles.prevSessionDate}>
                    {formatEndedAt(displayedSession.endedAt)}
                  </Caption>
                )}
              </View>
              {historySessions.length > 1 && (
                <View accessibilityRole="radiogroup">
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.sessionList}
                  >
                    {historySessions.map((s) => {
                      const active = s.id === displayedSession.id;
                      return (
                        <Button
                          key={s.id}
                          testID={`preview-session-${s.id}`}
                          variant={active ? 'primary' : 'outline'}
                          size="sm"
                          pill
                          onPress={() => setSelectedSessionId(s.id)}
                          accessibilityRole="radio"
                          accessibilityState={{ selected: active, checked: active }}
                          accessibilityLabel={formatEndedAt(s.endedAt)}
                        >
                          {formatSessionChipLabel(s.endedAt)}
                        </Button>
                      );
                    })}
                  </ScrollView>
                </View>
              )}
              <View style={styles.transcriptContainer}>
                <TranscriptPanel entries={previousSessionEntries} mode="replay" />
              </View>
            </View>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <HStack style={styles.header} spacing="md">
        {backControl}
        <VStack style={{ flex: 1 }} spacing="xs">
          <Heading2 style={styles.agentName}>
            {agentName ?? t('voiceAgents.previewCall.previewTitle')}
          </Heading2>
          <HStack spacing="sm" style={{ alignItems: 'center' }}>
            <Badge
              color={STATUS_BADGE_COLORS[connectionStatus] ?? 'neutral'}
              accessibilityLabel={`${t('voiceAgents.previewCall.statusPrefix')}: ${statusLabel(connectionStatus)}`}
            >
              {statusLabel(connectionStatus)}
            </Badge>
            {connectionStatus === 'connected' && (
              <Caption
                style={styles.timer}
                accessibilityLabel={`${t('voiceAgents.previewCall.elapsedPrefix')}: ${formatElapsed(elapsedSeconds)}`}
              >
                {formatElapsed(elapsedSeconds)}
              </Caption>
            )}
          </HStack>
        </VStack>
      </HStack>

      <View style={styles.mainArea}>
        <View style={styles.centerPanel}>
          {connectionStatus === 'error' ? (
            <VStack
              spacing="md"
              style={styles.errorBox}
              accessibilityLabel={t(
                errorKind === 'payment'
                  ? 'voiceAgents.previewCall.errorPayment.title'
                  : 'voiceAgents.previewCall.errorGeneric.title',
              )}
            >
              <Heading4 style={styles.headingCenter}>
                {t(
                  errorKind === 'payment'
                    ? 'voiceAgents.previewCall.errorPayment.title'
                    : 'voiceAgents.previewCall.errorGeneric.title',
                )}
              </Heading4>
              <Body style={styles.bodyCenter}>
                {t(
                  errorKind === 'payment'
                    ? 'voiceAgents.previewCall.errorPayment.body'
                    : 'voiceAgents.previewCall.errorGeneric.body',
                )}
              </Body>
              <Button
                variant="secondary"
                onPress={handleEnd}
                accessibilityLabel={t('voiceAgents.previewCall.endPreviewA11y')}
                accessibilityRole="button"
              >
                {t('voiceAgents.previewCall.endPreview')}
              </Button>
            </VStack>
          ) : (
            <>
              <AgentVoiceVisualizer isActive={isMicActive} isAgentSpeaking={isAgentSpeaking} />

              <Caption
                style={styles.hint}
                accessibilityLiveRegion="polite"
                accessibilityRole="text"
              >
                {isAgentSpeaking
                  ? t('voiceAgents.previewCall.agentSpeaking')
                  : isMicActive
                    ? t('voiceAgents.previewCall.listening')
                    : t('voiceAgents.previewCall.micPaused')}
              </Caption>

              <HStack spacing="lg" style={styles.controls}>
                <Pressable
                  style={[styles.iconBtn, isMicActive && styles.iconBtnActive]}
                  onPress={toggleMic}
                  accessible
                  accessibilityRole="button"
                  accessibilityLabel={
                    isMicActive
                      ? t('voiceAgents.previewCall.muteMicA11y')
                      : t('voiceAgents.previewCall.unmuteMicA11y')
                  }
                  accessibilityState={{ checked: isMicActive }}
                >
                  <Label style={[styles.iconBtnLabel, isMicActive && styles.iconBtnLabelActive]}>
                    {isMicActive
                      ? `🎙 ${t('voiceAgents.previewCall.mute')}`
                      : `🎙 ${t('voiceAgents.previewCall.unmute')}`}
                  </Label>
                </Pressable>

                <Button
                  variant="secondary"
                  onPress={handleEnd}
                  accessibilityLabel={t('voiceAgents.previewCall.endPreviewA11y')}
                  accessibilityRole="button"
                >
                  {t('voiceAgents.previewCall.endPreview')}
                </Button>
              </HStack>
            </>
          )}
        </View>

        <View style={styles.rightPanel}>
          <Heading4 style={styles.panelTitle} accessibilityRole="header">
            {t('voiceAgents.previewCall.liveTranscript')}
          </Heading4>
          <View style={styles.transcriptContainer}>
            <TranscriptPanel entries={transcriptEntries} mode="live" />
          </View>

          {toolCallLog.length > 0 && (
            <View style={styles.toolCallPanel}>
              <Heading4 style={styles.panelTitle} accessibilityRole="header">
                {t('voiceAgents.previewCall.toolCalls')}
              </Heading4>
              <ScrollView style={styles.toolCallScroll} contentContainerStyle={styles.toolCallList}>
                {toolCallLog.map((call, i) => (
                  <PreviewToolCallCard key={`${call.id}-${i}`} entry={call} />
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  backRow: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  idleLayout: {
    flex: 1,
    flexDirection: 'row',
  },
  prevSessionHeader: {
    padding: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  sessionList: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  prevSessionDate: {
    color: colors.text.tertiary,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  startScreen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  headingCenter: {
    color: colors.text.primary,
    textAlign: 'center',
  },
  bodyCenter: {
    color: colors.text.secondary,
    textAlign: 'center',
    maxWidth: 400,
  },
  header: {
    padding: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
    alignItems: 'flex-start',
  },
  agentName: {
    color: colors.text.primary,
  },
  timer: {
    color: colors.text.tertiary,
    fontVariant: ['tabular-nums'],
  },
  mainArea: {
    flex: 1,
    flexDirection: 'row',
  },
  centerPanel: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 24,
  },
  hint: {
    color: colors.text.tertiary,
    marginTop: 8,
  },
  errorBox: {
    alignItems: 'center',
    maxWidth: 440,
  },
  controls: {
    alignItems: 'center',
    marginTop: 8,
  },
  iconBtn: {
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.smPlus,
    borderRadius: tokens.radius.smPlus,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.background.elevated,
  },
  iconBtnActive: {
    borderColor: colors.primary.main,
    backgroundColor: colors.primary.muted,
  },
  iconBtnLabel: {
    color: colors.text.secondary,
    fontSize: 14,
  },
  iconBtnLabelActive: {
    color: colors.primary.main,
  },
  rightPanel: {
    width: 320,
    borderLeftWidth: 1,
    borderLeftColor: colors.background.elevated,
    backgroundColor: colors.background.secondary,
  },
  panelTitle: {
    color: colors.text.primary,
    padding: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  transcriptContainer: {
    flex: 1,
  },
  toolCallPanel: {
    maxHeight: 240,
    borderTopWidth: 1,
    borderTopColor: colors.background.elevated,
  },
  toolCallScroll: {
    flex: 1,
  },
  toolCallList: {
    padding: 12,
    gap: 8,
  },
});
